/**
 * AI利用クォータ管理のためのカスタムフック
 *
 * 月間トークン利用量の監視と制限チェックを提供
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { checkAIQuota } from '../services/api';
import { QuotaInfo, QuotaWarning } from '../types/api';

interface AIQuotaContextValue {
  quotaInfo: QuotaInfo | null;
  isLoading: boolean;
  isExceeded: boolean;
  quotaWarning: QuotaWarning | null;
  resetDate: string | null;
  refreshQuota: () => Promise<void>;
  updateQuotaFromWarning: (warning: QuotaWarning) => void;
}

const AIQuotaContext = createContext<AIQuotaContextValue | undefined>(
  undefined
);

interface AIQuotaProviderProps {
  children: ReactNode;
}

export function AIQuotaProvider({ children }: AIQuotaProviderProps) {
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [quotaWarning, setQuotaWarning] = useState<QuotaWarning | null>(null);
  const [resetDate, setResetDate] = useState<string | null>(null);

  const refreshQuota = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await checkAIQuota();
      setQuotaInfo(response.quota);
      setResetDate(response.reset_date);

      // ステータスに応じて警告をクリア
      if (response.status === 'ok') {
        setQuotaWarning(null);
      }
    } catch (error) {
      console.error('Failed to fetch AI quota:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateQuotaFromWarning = useCallback((warning: QuotaWarning) => {
    setQuotaWarning(warning);
    // 警告からクォータ情報を更新（サーバーから再取得するまでの暫定値）
    refreshQuota().catch(console.error);
  }, [refreshQuota]);

  useEffect(() => {
    // 初回ロード時にクォータ情報を取得
    refreshQuota().catch(console.error);

    // 定期的に更新(5分ごと)
    const interval = setInterval(() => {
      refreshQuota().catch(console.error);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [refreshQuota]);

  const isExceeded = quotaInfo ? quotaInfo.remaining_tokens <= 0 : false;

  const value: AIQuotaContextValue = {
    quotaInfo,
    isLoading,
    isExceeded,
    quotaWarning,
    resetDate,
    refreshQuota,
    updateQuotaFromWarning,
  };

  return (
    <AIQuotaContext.Provider value={value}>
      {children}
    </AIQuotaContext.Provider>
  );
}

export function useAIQuota(): AIQuotaContextValue {
  const context = useContext(AIQuotaContext);
  if (context === undefined) {
    throw new Error('useAIQuota must be used within an AIQuotaProvider');
  }
  return context;
}
